// src/features/patient/human-review/reviewQueues.ts

/**
 * Purpose : The patient's two review queues in one place, so the sidebar badges,
 *           both queue pages, the bulk-review screen, notifications and the
 *           Document Panel always agree on which item lives where. Identity
 *           checks ("Is this upload yours?") are patient-only — no clinician ever
 *           answers them — so they have their own Patient Only Queue; everything
 *           else (medicines, medicine pairs, record conflicts) is the Human Review Queue.
 *
 * Contents :
 *
 * * type(ReviewQueue) : "health" (Human Review Queue) or "identity" (Patient Only Queue)
 * * type(BulkTab) : which of a queue's items the bulk screen stacks
 * * const(QUEUE_PATHS) : each queue's page and bulk-review routes
 * * function(isIdentityItem) : whether an item is an identity check
 * * function(queueOf) : which queue an item belongs to
 * * function(itemsForQueue) : a queue's items, from the shared review list
 * * function(isOpenItem) : whether an item is waiting on the patient
 * * function(countAwaiting) : how many of a queue's items wait on the patient
 * * function(bulkReviewPath) : the bulk-review route, optionally for one upload
 * * function(isFromUpload) : whether an item came from a given upload
 */

import type { ReviewItem } from "@/portals/patient/api/reviewItemsApi";


// type(ReviewQueue)
export type ReviewQueue = "health" | "identity";

// type(BulkTab)
/** Which of a queue's items the bulk screen stacks: waiting (Flagged) or Skipped. */
export type BulkTab = "flagged" | "skipped";


// const(QUEUE_PATHS)
export const QUEUE_PATHS: Record<ReviewQueue, { page: string; bulk: string }> = {
  health: { page: "/patient/human-review-queue", bulk: "/patient/human-review-queue/review-all" },
  identity: { page: "/patient/patient-only-queue", bulk: "/patient/patient-only-queue/review-all" },
};


// function(isIdentityItem)
/**
 * Whether an item is an identity check.
 *
 * @param item - A review item.
 * @returns True for "Is this upload yours?" (IDENTITY_MISMATCH).
 */
export function isIdentityItem(item: ReviewItem): boolean {
  return item.triggerType === "IDENTITY_MISMATCH";
}


// function(queueOf)
/**
 * Which queue an item belongs to.
 *
 * @param item - A review item.
 * @returns "identity" for an identity check, otherwise "health".
 */
export function queueOf(item: ReviewItem): ReviewQueue {
  return isIdentityItem(item) ? "identity" : "health";
}


// function(itemsForQueue)
/**
 * A queue's items, in the list's own order.
 *
 * @param items - Every review item of the profile (the shared review list).
 * @param queue - The queue to keep.
 * @returns The items that belong to that queue.
 */
export function itemsForQueue(items: ReviewItem[], queue: ReviewQueue): ReviewItem[] {
  return items.filter((item) => queueOf(item) === queue);
}


// function(isOpenItem)
/**
 * Whether an item is waiting on the patient (its queue's Flagged tab).
 *
 * @param item - A review item.
 * @returns True while the patient still needs to answer it.
 */
export function isOpenItem(item: ReviewItem): boolean {
  return item.awaitingInput;
}


// function(countAwaiting)
/**
 * How many of a queue's items wait on the patient (its sidebar badge).
 *
 * @param items - Every review item of the profile.
 * @param queue - The queue to count.
 * @returns The number of that queue's items waiting on the patient.
 */
export function countAwaiting(items: ReviewItem[], queue: ReviewQueue): number {
  return itemsForQueue(items, queue).filter(isOpenItem).length;
}


// function(bulkReviewPath)
/**
 * The bulk-review route of a queue.
 *
 * @param queue - The queue to review.
 * @param uploadId - Only the items from this upload (from the Document Panel); omit for all.
 * @param tab - "skipped" for the Skipped items; omit for the waiting ones.
 * @returns The route, with ?upload= / ?tab= when given.
 */
export function bulkReviewPath(queue: ReviewQueue, uploadId?: string | null, tab: BulkTab = "flagged"): string {
  const params = [
    uploadId ? `upload=${encodeURIComponent(uploadId)}` : null,
    tab === "skipped" ? "tab=skipped" : null,
  ].filter(Boolean);

  return params.length ? `${QUEUE_PATHS[queue].bulk}?${params.join("&")}` : QUEUE_PATHS[queue].bulk;
}


// function(isFromUpload)
/**
 * Whether an item came from an upload: the identity check of that upload, or
 * any record (or record-conflict option) it is about.
 *
 * @param item - A review item.
 * @param uploadId - The upload to match.
 * @returns True when the item involves that upload.
 */
export function isFromUpload(item: ReviewItem, uploadId: string): boolean {
  if (item.empi?.identity?.uploadId === uploadId) return true;

  if (item.records.some((record) => record.uploadId === uploadId)) return true;

  return Boolean(item.empi?.conflict?.options.some((option) => option.uploadId === uploadId));
}
